"use client";

import { useState } from "react";
import type { ReactNode } from "react";

interface PostFaqItem {
  q: string;
  a: ReactNode;
}

interface Props {
  faqs: PostFaqItem[];
}

/** Compact FAQ accordion rendered inside the article column, right under the
    post content — same accordion styling as the product FaqSection, without
    the full-width section chrome. */
export default function PostFaq({ faqs }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="mt-14">
      <h2 className="text-foreground text-2xl font-bold tracking-tight">
        Frequently asked questions
      </h2>
      <div className="mt-6 space-y-3">
        {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={faq.q} className="border-border bg-card overflow-hidden rounded-xl border">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="text-foreground flex w-full cursor-pointer items-center justify-between px-5 py-4 text-left text-sm font-semibold select-none"
              >
                {faq.q}
                <span
                  className={`text-muted-foreground ml-4 shrink-0 transition-transform duration-300 ease-out ${isOpen ? "rotate-180" : ""}`}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="size-4"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
              >
                <div className="overflow-hidden">
                  <div className="text-muted-foreground px-5 pt-1 pr-10 pb-5 text-sm leading-relaxed">
                    {faq.a}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
